"use client"
import Card from '@/components/atomic/Card'
import React, { useState, useEffect } from 'react'
import SearchTripDropDown from './SearchTripDropDown'
import { ICONS } from '@/constants/constants'
import Image from 'next/image'
import TripCalendar from './SearchTripDropdown/TripCalendar'
import PassengerSelector from './SearchTripDropdown/PassengerSelector'
import VehicleSelector from './SearchTripDropdown/VehicleSelector'
import { useAppDispatch, useAppSelector } from '@/lib/hooks/redux'
import { useRouter } from 'next/navigation'
import {
    fetchRoutes,
    searchTrips,
    setTripType,
    setFromLocation,
    setToLocation,
    setDepartureDate,
    setReturningDate,
    setPassengerData,
    setVehicleData,
    setVehicleCount,
    resetToInitialState
} from '@/lib/slices/searchTripSlice'
import { useToastContext } from '@/components/ui/ToastProvider';
const SearchTrip = () => {
    const dispatch = useAppDispatch()
    const router = useRouter()
    const {
        searchData,
        routes,
        locations,
        tripType,
        isLoading,
        vehicleCount
    } = useAppSelector(state => state.searchTrip)
    const { toast } = useToastContext();
    const [showDepartingCalendar, setShowDepartingCalendar] = useState(false)
    const [showReturningCalendar, setShowReturningCalendar] = useState(false)
    const [showPassengerSelector, setShowPassengerSelector] = useState(false)
    const [showVehicleSelector, setShowVehicleSelector] = useState(false)
    const [departingTrigger, setDepartingTrigger] = useState<HTMLElement | null>(null)
    const [returningTrigger, setReturningTrigger] = useState<HTMLElement | null>(null)
    const [passengerTrigger, setPassengerTrigger] = useState<HTMLElement | null>(null)
    const [vehicleTrigger, setVehicleTrigger] = useState<HTMLElement | null>(null)

    // Helper function to format date from YYYY-MM-DD to DD/MM/YYYY for display
    const formatDateForDisplay = (dateString: string) => {
        const [year, month, day] = dateString.split('-')
        return `${day}/${month}/${year}`
    }

    // Reset to initial state and fetch routes on component mount
    useEffect(() => {
        dispatch(resetToInitialState())
        dispatch(fetchRoutes())
    }, [dispatch])

    // Get current from/to locations for display
    const getCurrentFromLocation = () => {
        const route = routes.find(r => r.id === searchData.routeId)
        return route?.departure || locations[0] || ''
    }

    const getCurrentToLocation = () => {
        const route = routes.find(r => r.id === searchData.routeId)
        return route?.destination || locations[1] || ''
    }

    // Handle route switching - when From changes, To automatically changes to the other location
    const handleFromChange = (selectedFrom: string) => {
        dispatch(setFromLocation(selectedFrom))
    }

    // Handle reverse route switching - when To changes, From automatically changes to the other location
    const handleToChange = (selectedTo: string) => {
        dispatch(setToLocation(selectedTo))
    }

    // Handle departure date selection
    const handleDepartureSelect = (date: string) => {
        dispatch(setDepartureDate(date))
    }

    // Handle returning date selection
    const handleReturningSelect = (date: string) => {
        dispatch(setReturningDate(date))
    }

    // Handle trip type change
    const handleTripTypeChange = (type: 'roundtrip' | 'oneway') => {
        dispatch(setTripType(type))
    }

    // Handle passenger selection
    const handlePassengerSelect = (passengerString: string) => {
        // This just updates the display count
        // The actual detailed data is handled by handlePassengerDataUpdate
    }

    // Handle detailed passenger data updates
    const handlePassengerDataUpdate = (data: { adult: number; senior: number; child: number; special_assistance: boolean; special_assistance_reason?: string }) => {
        dispatch(setPassengerData(data))
    }

    // Handle vehicle selection
    const handleVehicleSelect = (vehicleString: string) => {
        const count = parseInt(vehicleString) || 0
        dispatch(setVehicleCount(count))
    }

    // Handle vehicle data updates (type, same each way, and size)
    const handleVehicleDataUpdate = (data: { type: string; sameEachWay: boolean; height?: string; length?: string }) => {
        dispatch(setVehicleData({
            type: data.type,
            vehicle_return: data.sameEachWay,
            height: data.type === 'Foot Passenger' ? 'none' : (data.height || 'standard'),
            length: data.type === 'Foot Passenger' ? 'none' : (data.length || 'standard')
        }))
    }

    // Check if search should be disabled
    const isSearchDisabled = () => {
        const totalPassengers = searchData.passenger.adult + searchData.passenger.senior + searchData.passenger.child
        return totalPassengers === 0 && vehicleCount === 0
    }

    // Handle search button click
    const handleSearch = async () => {
        // Don't proceed if both passengers and vehicles are 0
        if (isSearchDisabled()) {
            return
        }

        try {
            // Clear previous trip results before starting new search
            const { clearTripResults } = await import('@/lib/slices/tripResultsSlice')
            dispatch(clearTripResults())

            const result = await dispatch(searchTrips()).unwrap()

            // Check for success in different possible response structures
            if (result.success || result.status === true || result.data) {
                // Add a small delay to ensure Redux state is updated
                router.push('/booking')

            } else {
            }
        } catch (error) {
            toast.error('An error occurred during the search. Please try again.')
        }
    }

    return (
        <div className='w-full font-work-sans'>
            {/* Trip Type Selector */}
            <div className='flex bg-white w-fit ml-4 rounded-t-lg overflow-hidden shadow-sm'>
                <button
                    className={`px-4 py-1 text-sm font-medium transition-colors ${tripType === 'roundtrip'
                        ? 'bg-white text-gray-600 hover:bg-gray-50' :
                        'bg-[#BFBFBF] text-gray-900 '
                        }`}
                    onClick={() => handleTripTypeChange('roundtrip')}
                >
                    Round Trip
                </button>
                <button
                    className={`px-4 py-1 text-sm font-medium transition-colors ${tripType === 'oneway'
                        ? 'bg-white text-gray-600 hover:bg-gray-50' :
                        'bg-[#BFBFBF] text-gray-900 '
                        }`}
                    onClick={() => handleTripTypeChange('oneway')}
                >
                    One Way
                </button>
            </div>

            {/* Search Form */}
            <Card className="p-4 w-full overflow-visible">
                <div className="flex w-full flex-wrap  gap-2 items-end overflow-visible">
                    <div className="flex-1 min-w-[140px] max-w-full xl:max-w-none">
                        <SearchTripDropDown
                            label="From"
                            value={getCurrentFromLocation()}
                            placeholder="Departure"
                            options={locations}
                            onSelect={handleFromChange}
                        />
                    </div>
                    <div className='pb-2 hidden lg:flex justify-center items-center flex-shrink-0 w-8'>
                        {
                            tripType === 'roundtrip' ? (
                                <Image
                                    src={ICONS.arrows}
                                    alt='next'
                                    width={20}
                                    height={20}

                                />
                            ) : (
                                <Image
                                    src={ICONS.singlearrow}
                                    alt='next'
                                    width={20}
                                    height={20}
                                    style={{ transform: 'rotate(180deg)' }}
                                />
                            )
                        }
                    </div>
                    <div className="flex-1 min-w-[140px] max-w-full xl:max-w-none">
                        <SearchTripDropDown

                            label="To"
                            value={getCurrentToLocation()}
                            placeholder="Destination"
                            options={locations}
                            onSelect={handleToChange}
                        />
                    </div>

                    <div className="flex-1 min-w-[120px] max-w-full xl:max-w-none">
                        <SearchTripDropDown
                            label="Passengers"
                            value={(searchData.passenger.adult + searchData.passenger.senior + searchData.passenger.child).toString()}
                            placeholder="1"
                            icon={ICONS.passenger}
                            showDropdownArrow={true}
                            isDropdownOpen={showPassengerSelector}
                            onClick={(event) => {
                                setPassengerTrigger(event.currentTarget)
                                setShowPassengerSelector(!showPassengerSelector)
                            }}
                        />
                    </div>

                    <div className="flex-1 min-w-[120px] max-w-full xl:max-w-none">
                        <SearchTripDropDown
                            label="Vehicles"
                            value={vehicleCount === 0 ? "0" : `${vehicleCount}`}
                            placeholder="0"
                            icon={ICONS.vehicle}
                            showDropdownArrow={true}
                            isDropdownOpen={showVehicleSelector}
                            onClick={(event) => {
                                setVehicleTrigger(event.currentTarget)
                                setShowVehicleSelector(!showVehicleSelector)
                            }}
                        />
                    </div>

                    <div className="flex-1 min-w-[140px] max-w-full xl:max-w-none">
                        <SearchTripDropDown
                            label="Departing"
                            value={formatDateForDisplay(searchData.departure)}
                            placeholder="Select date"
                            icon={ICONS.tripcalendar}
                            showDropdownArrow={false}
                            onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                const target = event.currentTarget
                                if (target) {
                                    setDepartingTrigger(target)
                                    setShowDepartingCalendar(!showDepartingCalendar)
                                }
                            }}
                        />
                    </div>

                    {tripType === 'roundtrip' && (
                        <div className="flex-1 min-w-[140px] max-w-full xl:max-w-none">
                            <SearchTripDropDown
                                label="Returning"
                                value={searchData.returning ? formatDateForDisplay(searchData.returning) : ''}
                                placeholder="Select date"
                                icon={ICONS.tripcalendar}
                                showDropdownArrow={false}
                                onClick={(event) => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    const target = event.currentTarget
                                    if (target) {
                                        setReturningTrigger(target)
                                        setShowReturningCalendar(!showReturningCalendar)
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* Render calendars using portal */}
                    {showDepartingCalendar && departingTrigger && (
                        <TripCalendar
                            triggerElement={departingTrigger}
                            onDateSelect={handleDepartureSelect}
                            onClose={() => setShowDepartingCalendar(false)}
                            selectedDate={formatDateForDisplay(searchData.departure)}
                        />
                    )}

                    {showReturningCalendar && tripType === 'roundtrip' && returningTrigger && (
                        <TripCalendar
                            triggerElement={returningTrigger}
                            onDateSelect={handleReturningSelect}
                            onClose={() => setShowReturningCalendar(false)}
                            selectedDate={searchData.returning ? formatDateForDisplay(searchData.returning) : ''}
                            minDate={formatDateForDisplay(searchData.departure)}
                        />
                    )}

                    {showPassengerSelector && (
                        <PassengerSelector
                            triggerElement={passengerTrigger}
                            onPassengerSelect={handlePassengerSelect}
                            onPassengerDataUpdate={handlePassengerDataUpdate}
                            onClose={() => setShowPassengerSelector(false)}
                            currentPassengers={(searchData.passenger.adult + searchData.passenger.senior + searchData.passenger.child).toString()}
                        />
                    )}

                    {showVehicleSelector && (
                        <VehicleSelector
                            triggerElement={vehicleTrigger}
                            onVehicleSelect={handleVehicleSelect}
                            onVehicleDataUpdate={handleVehicleDataUpdate}
                            onClose={() => setShowVehicleSelector(false)}
                            currentVehicles={vehicleCount.toString()}
                            departingDate={formatDateForDisplay(searchData.departure)}
                        />
                    )}

                    <div className="flex-shrink-0 flex flex-col w-full xl:w-auto xl:min-w-[200px]">
                        <div className="h-3 mb-1"></div>
                        <button
                            onClick={handleSearch}
                            disabled={isLoading || isSearchDisabled()}
                            className={`px-6 py-1 rounded-md font-medium transition-colors whitespace-nowrap text-sm h-[35px] w-full ${isLoading || isSearchDisabled()
                                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                : 'bg-gray-900 text-white hover:bg-gray-800 cursor-pointer'
                                }`}
                        >
                            {isLoading
                                ? 'Searching...'
                                : isSearchDisabled()
                                    ? 'Search Trips'
                                    : 'Search Trips'
                            }
                        </button>
                    </div>


                </div>


            </Card>
        </div>
    )
}

export default SearchTrip